import { Divider, Grid, Link, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ETenderStatus, ITender } from 'types/tender'
import AssignmentIcon from '@mui/icons-material/Assignment'
import { formatTimeStampToDate } from 'hooks/date/formattedTime'
import { Link as RouteLink } from 'react-router-dom'
import { useMemo } from 'react'
import WorkspacesIcon from '@mui/icons-material/Workspaces'
import EditCalendarIcon from '@mui/icons-material/EditCalendar'
import PublicIcon from '@mui/icons-material/Public'
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom'
import DomainVerificationIcon from '@mui/icons-material/DomainVerification'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import GroupsIcon from '@mui/icons-material/Groups'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DoDisturbIcon from '@mui/icons-material/DoDisturb'
import AlarmOnIcon from '@mui/icons-material/AlarmOn'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import MemoryIcon from '@mui/icons-material/Memory'

type Props = {
  tender: ITender
  onClick?: (tender: ITender) => void
}

export const TenderCard = ({ tender, onClick }: Props) => {
  const { t } = useTranslation()

  const tenderLink = `/portal/tender-participation/${tender.id}`

  const icon = useMemo(() => {
    const props = { sx: { fontSize: 48, color: 'purple' } }

    switch (tender.status) {
      case ETenderStatus.CREATED: {
        return <AssignmentIcon {...props} />
      }
      case ETenderStatus.PLANNING: {
        return <WorkspacesIcon {...props} />
      }
      case ETenderStatus.MODIFIED: {
        return <EditCalendarIcon {...props} />
      }
      case ETenderStatus.PUBLISHED: {
        return <PublicIcon {...props} />
      }
      case ETenderStatus.UNDER_EVALUATION: {
        return <HourglassBottomIcon {...props} />
      }
      case ETenderStatus.EVALUATION_SUBMITTED: {
        return <DomainVerificationIcon {...props} />
      }
      case ETenderStatus.WINNER_SELECTED: {
        return <EmojiEventsIcon {...props} />
      }
      case ETenderStatus.COMMISSION_EVALUATION: {
        return <GroupsIcon {...props} />
      }
      case ETenderStatus.COMMITTEE_REVIEW: {
        return <GroupsIcon {...props} />
      }
      case ETenderStatus.COMPLETED: {
        return <CheckCircleOutlineIcon {...props} />
      }
      case ETenderStatus.CANCELLED: {
        return <DoDisturbIcon {...props} />
      }
      case ETenderStatus.READY_FOR_BIDDING: {
        return <AlarmOnIcon {...props} />
      }
      case ETenderStatus.UNDER_BIDDING: {
        return <PlayCircleOutlineIcon {...props} />
      }
      case ETenderStatus.BIDDING_EVALUATION: {
        return <MemoryIcon {...props} />
      }
    }
  }, [tender.status])

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={3}
      bgcolor="#FFFFFF"
      padding={2}
      borderRadius={1}
      marginTop={2}
      borderLeft={4}
      borderColor="primary.main"
      boxShadow={2}
    >
      {icon}
      <Stack width="100%" spacing={1}>
        <Link
          component={RouteLink}
          to={tenderLink}
          onClick={(e) => {
            if (onClick) {
              e.preventDefault()
              onClick(tender)
            }
          }}
          underline="hover"
        >
          <Typography variant="subtitle2" fontWeight="bold" color="primary">
            {tender.name}
          </Typography>
        </Link>
        <Divider flexItem />
        <Grid container>
          <Grid item xs={3}>
            <Typography variant="caption" fontWeight="bold">
              {t('status')}:
            </Typography>
            <Typography variant="body2">
              {t(`tender-status-${tender.status}`)}
            </Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="caption" fontWeight="bold">
              {t('procurementMethod')}:
            </Typography>
            <Typography variant="body2">{t(tender.method)}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" fontWeight="bold">
              {t('startsAt')}:
            </Typography>
            <Typography variant="body2">
              {tender.startTime ? formatTimeStampToDate(tender.startTime) : '-'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" fontWeight="bold">
              {t('endDate')}:
            </Typography>
            <Typography variant="body2">
              {tender.envelopeOpeningTime
                ? formatTimeStampToDate(tender.envelopeOpeningTime)
                : '-'}
            </Typography>
          </Grid>
        </Grid>
      </Stack>
    </Stack>
  )
}
